import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getTenants } from '../services/tenantService';
import { tenantReturnPath } from '../utils/tenantRouting';
import RSMinSignInButton from '../components/RSMinSignInButton';

/**
 * TenantLoginPage — per-tenant branded login page.
 *
 * Route: /t/:tenantSlug/login  (public — no auth required)
 *
 * Community staff/members sign in under their community's branding rather
 * than the CIP platform brand. On success they land on their community
 * requested community page, or their community dashboard by default.
 */
const TenantLoginPage = () => {
  const { tenantSlug } = useParams();
  const { login, user, memberships = [] } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const returnPath = tenantReturnPath(tenantSlug, location.search);

  const [tenant, setTenant]     = useState(null);
  const [loading, setLoading]   = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [revision, setRevision] = useState(0);

  const [form, setForm]   = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [busy, setBusy]   = useState(false);

  // Branding comes from the public tenants list — no auth needed
  useEffect(() => {
    let current = true;
    setLoading(true);
    setTenant(null);
    setNotFound(false);
    setLoadError('');
    getTenants()
      .then((res) => {
        if (!current) return;
        const match = (res.data || []).find((t) => t.slug === tenantSlug);
        if (match) setTenant(match);
        else setNotFound(true);
      })
      .catch((err) => { if (current) setLoadError(err.response?.data?.message || 'Unable to load this community. Please retry.'); })
      .finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
  }, [tenantSlug, revision]);

  // Wait for bootstrap to confirm sign-in before restoring the community page.
  useEffect(() => {
    if (!user || !tenant) return;
    const canEnter = ['cip_admin', 'cip_staff'].includes(user.globalRole)
      || memberships.some((entry) => entry.status === 'active' && entry.tenantSlug === tenantSlug);
    navigate(canEnter ? returnPath : `/t/${tenantSlug}/join`, { replace: true });
  }, [user, tenant, memberships, tenantSlug, returnPath, navigate]);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      await login(form.email, form.password);
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="page-loading">Loading…</div>;
  if (loadError) return <div className="tenant-login" role="alert"><p>{loadError}</p>
    <button type="button" onClick={() => setRevision((value) => value + 1)}>Retry community</button></div>;

  if (notFound) {
    return (
      <div className="tenant-login">
        <div className="tenant-login__card">
          <h2>Community Not Found</h2>
          <p>The community <code>{tenantSlug}</code> does not exist or is not active.</p>
          <Link to="/login">Go to platform login</Link>
        </div>
      </div>
    );
  }

  const displayName  = tenant.branding?.displayName || tenant.name;
  const logoUrl      = tenant.branding?.logoUrl;
  const primaryColor = tenant.branding?.primaryColor || '#2563eb';
  const bannerText   = tenant.branding?.bannerText;

  return (
    <div className="tenant-login" style={{ '--tenant-color': primaryColor }}>
      <div className="tenant-login__card" style={{ borderTopColor: primaryColor }}>
        <div className="tenant-login__brand">
          {logoUrl && <img src={logoUrl} alt={`${displayName} logo`} className="tenant-login__logo" />}
          <h1>{displayName}</h1>
          {bannerText && <p className="tenant-login__banner">{bannerText}</p>}
          {tenant.description && <p className="tenant-login__desc">{tenant.description}</p>}
        </div>

        <h2>Sign in</h2>
        {location.state?.passwordChanged && <p role="status">Your CIP password was changed. Please sign in again.</p>}
        <RSMinSignInButton tenantSlug={tenantSlug} search={location.search} />
        {error && <div className="error-msg" role="alert">{error}</div>}
        <form onSubmit={handleSubmit}>
          <label>Email<input name="email" type="email" placeholder="Email" value={form.email} onChange={handleChange} required autoComplete="email" /></label>
          <label>Password<input name="password" type="password" placeholder="Password" value={form.password} onChange={handleChange} required autoComplete="current-password" /></label>
          <button type="submit" disabled={busy} style={{ background: primaryColor }}>
            {busy ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        <p className="tenant-login__foot">
          New here? <Link to={`/t/${tenantSlug}/join`}>Register</Link>
        </p>
      </div>
      <p className="tenant-login__powered">
        Powered by <Link to="/">Civic Intelligence Platform</Link>
      </p>
    </div>
  );
};

export default TenantLoginPage;
